import React from 'react';
import { motion } from 'framer-motion';

/**
 * 页面过渡动画组件
 * @param {Object} props
 * @param {React.ReactNode} props.children - 子组件
 * @param {string} [props.className=''] - 自定义类名
 */
export const PageTransition = ({ children, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ duration: 0.3, ease: 'easeInOut' }}
    className={className}
  >
    {children}
  </motion.div>
);

/**
 * 淡入动画组件
 * @param {Object} props
 * @param {React.ReactNode} props.children - 子组件
 * @param {number} [props.delay=0] - 延迟时间(秒)
 * @param {string} [props.className=''] - 自定义类名
 */
export const FadeIn = ({ children, delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.5, delay }}
    className={className}
  >
    {children}
  </motion.div>
);

/**
 * 上滑动画组件
 * @param {Object} props
 * @param {React.ReactNode} props.children - 子组件
 * @param {number} [props.delay=0] - 延迟时间(秒)
 * @param {string} [props.className=''] - 自定义类名
 */
export const SlideUp = ({ children, delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay, ease: 'easeOut' }}
    className={className}
  >
    {children}
  </motion.div>
);

/**
 * 缩放动画组件
 * @param {Object} props
 * @param {React.ReactNode} props.children - 子组件
 * @param {number} [props.delay=0] - 延迟时间(秒)
 * @param {string} [props.className=''] - 自定义类名
 */
export const ScaleIn = ({ children, delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.4, delay, ease: 'easeOut' }}
    className={className}
  >
    {children}
  </motion.div>
);

/**
 * 交错动画容器组件
 * @param {Object} props
 * @param {React.ReactNode} props.children - 子组件
 * @param {number} [props.staggerDelay=0.1] - 子元素交错延迟(秒)
 * @param {string} [props.className=''] - 自定义类名
 */
export const StaggerContainer = ({ 
  children, 
  staggerDelay = 0.1, 
  className = '' 
}) => (
  <motion.div
    initial="hidden"
    animate="visible"
    variants={{
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: staggerDelay
        }
      }
    }}
    className={className}
  >
    {children}
  </motion.div>
);

/**
 * 交错动画子项组件
 * @param {Object} props
 * @param {React.ReactNode} props.children - 子组件
 * @param {string} [props.className=''] - 自定义类名
 */
export const StaggerItem = ({ children, className = '' }) => (
  <motion.div
    variants={{
      hidden: { opacity: 0, y: 20 },
      visible: { 
        opacity: 1, 
        y: 0,
        transition: { duration: 0.4, ease: 'easeOut' }
      }
    }}
    className={className}
  >
    {children}
  </motion.div>
);

/**
 * 悬停缩放组件
 * @param {Object} props
 * @param {React.ReactNode} props.children - 子组件
 * @param {number} [props.scale=1.05] - 悬停缩放比例
 * @param {string} [props.className=''] - 自定义类名
 */
export const HoverScale = ({ children, scale = 1.05, className = '' }) => (
  <motion.div
    whileHover={{ scale }}
    whileTap={{ scale: 0.98 }}
    transition={{ duration: 0.2 }}
    className={className}
  >
    {children}
  </motion.div>
);

/**
 * 脉冲动画组件
 * @param {Object} props
 * @param {React.ReactNode} props.children - 子组件
 * @param {string} [props.className=''] - 自定义类名
 */
export const Pulse = ({ children, className = '' }) => (
  <motion.div
    animate={{ scale: [1, 1.05, 1] }}
    transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
    className={className}
  >
    {children}
  </motion.div>
);

/**
 * 浮动动画组件
 * @param {Object} props
 * @param {React.ReactNode} props.children - 子组件
 * @param {number} [props.y=10] - 浮动高度(px)
 * @param {string} [props.className=''] - 自定义类名
 */
export const Float = ({ children, y = 10, className = '' }) => (
  <motion.div
    animate={{ y: [0, -y, 0] }}
    transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
    className={className}
  >
    {children}
  </motion.div>
);

export default {
  PageTransition,
  FadeIn,
  SlideUp,
  ScaleIn,
  StaggerContainer,
  StaggerItem,
  HoverScale,
  Pulse,
  Float
};
